// A part resting on a room's benches (`benchRest`: an infrared emitter on the
// highest seat under it) and what a part takes out of a room's backrests
// (`cutsBackrest`: the emitter in its own backrest, the room's boards and the
// strips behind them stopping at its sides). Derived from the parts and the
// benches every edit (main.ts `reconcileFixtures`), never typed. Pure and
// DOM-free (test/benchRest.test.mjs; docs/claude/products.md, *Infrared
// emitters*).

import type {
  BackrestCut,
  ProductInstance,
  Vec2,
} from '../shared/types';
import { roomBenchLines } from './benchSolids';
import {
  floorOccupants,
  standingFoot,
} from './floorOccupancy';
import { pointInPolygon } from './geometry';
import { roomBase } from './heights';
import { isTilted } from './tilt';

/** Whether two plan polygons overlap (more than touch). */
export function polygonsOverlap(a: readonly Vec2[], b: readonly Vec2[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  const within = (p: Vec2, ring: readonly Vec2[]): boolean => pointInPolygon(p, ring as Vec2[]) && edgeGap(p, ring) > 1e-6;
  if (a.some((p) => within(p, b)) || b.some((p) => within(p, a))) return true;
  const side = (p: Vec2, q: Vec2, r: Vec2): number => Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  for (let i = 0; i < a.length; i++) {
    const p = a[i], q = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const r = b[j], s = b[(j + 1) % b.length];
      const s1 = side(p, q, r), s2 = side(p, q, s), s3 = side(r, s, p), s4 = side(r, s, q);
      if (s1 * s2 < 0 && s3 * s4 < 0) return true;
    }
  }
  return false;
}

/** How far a point lies from a ring's outline. */
function edgeGap(p: Vec2, ring: readonly Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2)) : 0;
    best = Math.min(best, Math.hypot(p.x - a.x - (b.x - a.x) * t, p.y - a.y - (b.y - a.y) * t));
  }
  return best;
}

/** The highest seat (mm over the room's floor) whose plan a footprint (room
 *  local) stands over: every bench of the room, its wall bands, its rounded
 *  corner pieces and its partitions' — a stepped profile's lift included. Null
 *  where it stands over none. */
export function seatUnder(room: ProductInstance, foot: readonly Vec2[]): number | null {
  if (foot.length < 3) return null;
  let best: number | null = null;
  for (const line of roomBenchLines(room)) {
    const back = line.path(0), front = line.path(line.depth);
    if (back.length < 2 || front.length < 2) continue;
    if (polygonsOverlap(foot, [...back, ...[...front].reverse()]) && (best == null || line.top > best)) best = line.top;
  }
  return best;
}

/** Where a part resting on the benches (`benchRest`) goes: its Height from
 *  ground with its underside on the highest seat under it (whole mm), or null —
 *  no bench under it, an upright part, or one that does not rest on benches. */
export function benchRestBase(room: ProductInstance, part: ProductInstance): number | null {
  if (part.benchRest !== 'above' || part.autoBenches || isTilted(part)) return null;
  const seat = seatUnder(room, standingFoot(room, part));
  return seat == null ? null : Math.round(roomBase(room) + seat);
}

/** What the parts that interrupt backrests (`cutsBackrest`) standing in a room
 *  take out of its backrests: each one's footprint in the room (room local) and
 *  its height band over the room's floor. */
export function backrestCutsOf(room: ProductInstance, parts: readonly ProductInstance[]): BackrestCut[] {
  return floorOccupants(room, parts)
    .filter((o) => o.part.cutsBackrest && !isTilted(o.part))
    .map((o) => ({ poly: o.foot, z0: o.z0, z1: o.z1 }));
}
